import React, { useState, useMemo } from "react";
import { OptimizedNumberCell, OptimizedColorCell } from "./OptimizedCell";
import { useGlobalData } from "../hooks/useGlobalHooks";
import { AlpacaPositionComparisonModal } from "./AlpacaPositionComparisonModal";
import AlpacaBulkOrderModal from "./AlpacaBulkOrderModal";
import { AlpacaBulkModifyModal } from "./AlpacaBulkModifyModal";
import { AlpacaGroupSelectionModal } from "./AlpacaGroupSelectionModal";
import CancelConfirmationModal from "./CancelConfirmationModal";
import AlpacaExitSuccessModal from "./AlpacaExitSuccessModal";
import apiClient from "../utils/apiClient";
import server from "../utils/serverConfig";
import { toast } from "react-hot-toast";

function getBrokerBadge(broker) {
    return { text: "AL", class: "bg-[#FCD72B] text-black" };
}

function formatNumber(num) {
    if (num === undefined || num === null) return "-";
    return num.toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 });
}

export function AlpacaPositionsView({
    loading,
    data = [],
    baseQtyMap = {},
    symbolStrategyBaseQty = {},
    activeAssets = [],
    positions = [],
    orders = []
}) {
    const { users } = useGlobalData();

    const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
    const [searchQuery, setSearchQuery] = useState("");

    // Modal states
    const [modalOpen, setModalOpen] = useState(false);
    const [selectedClient, setSelectedClient] = useState(null);
    const [bulkModalOpen, setBulkModalOpen] = useState(false);
    const [bulkModifyOpen, setBulkModifyOpen] = useState(false);
    const [isSelectionModalOpen, setIsSelectionModalOpen] = useState(false);
    const [ordersToModify, setOrdersToModify] = useState([]);

    // Cancel states
    const [confirmModalOpen, setConfirmModalOpen] = useState(false);
    const [successModalOpen, setSuccessModalOpen] = useState(false);
    const [successModalActionType, setSuccessModalActionType] = useState("Cancel");
    const [ordersToCancel, setOrdersToCancel] = useState([]);
    const [submittedOrders, setSubmittedOrders] = useState([]);

    // -- Cancel groups by asset (pending alpaca orders only) --
    const cancelGroups = useMemo(() => {
        if (!orders || orders.length === 0) return { assets: {} };
        const assets = {};
        orders.forEach(o => {
            if (!o || String(o.broker || "").toLowerCase() !== "alpaca") return;
            const statusStr = String(o.status || "").toLowerCase();
            const isPending = ["new", "partially_filled", "accepted", "pending_new", "held", "pending_replace", "pending", "open", "not sent"].includes(statusStr) || String(o.status_no) === "6";
            if (!isPending) return;

            const s = String(o.symbol || "").toUpperCase();
            const match = s.match(/^([A-Z]+)\d/);
            const asset = match ? match[1] : s;
            if (!assets[asset]) assets[asset] = [];
            assets[asset].push(o);
        });
        return { assets };
    }, [orders]);

    const openCancelModal = (ordersGroup) => {
        setOrdersToCancel(ordersGroup);
        setConfirmModalOpen(true);
    };

    const handleConfirmCancel = async () => {
        if (!ordersToCancel.length) { setConfirmModalOpen(false); return; }
        try {
            setSuccessModalActionType("Cancel");
            const payloads = ordersToCancel.map(o => ({
                orderid: String(o.order_id || o.orderId),
                clientid: o.clientId || o.client_id,
                broker: "alpaca"
            }));
            const res = await apiClient.post(`${server.godseye.baseUrl}manual_cancel_orders`, payloads);
            const rawOrders = res.data?.data || res.data?.orders || payloads;
            if (rawOrders?.length) {
                setSubmittedOrders(rawOrders.map(o => {
                    const match = ordersToCancel.find(c => String(c.orderId || c.order_id) === String(o.orderid || o.order_id || o.orderId));
                    const isSuccess = o.status === "success" || o.status === "SUCCESS";
                    const isFail = o.status === "failed" || o.status === "FAILED" || o.status === "rejected";
                    return {
                        ...o,
                        orderId: o.orderid || o.order_id || o.orderId,
                        clientId: o.clientid || o.client_id || o.clientId,
                        status: isFail ? "Rejected" : (isSuccess ? "CANCELED" : (o.status || "CANCELED")),
                        broker: o.broker || "alpaca",
                        symbol: match?.symbol || ordersToCancel[0]?.symbol || "N/A",
                        rejectReason: o.message || o.response?.message || (isFail ? "Cancellation failed" : "")
                    };
                }));
                setSuccessModalActionType("Cancel");
                setSuccessModalOpen(true);
            } else {
                toast.success("Alpaca cancellation processed!");
            }
        } catch (error) {
            console.error("❌ Alpaca cancel error:", error);
            toast.error("Failed to cancel orders");
        } finally {
            setConfirmModalOpen(false);
        }
    };

    const handleRemoveFromBatch = (orderId) => {
        setOrdersToCancel(prev => {
            const next = prev.filter(o => String(o.order_id || o.orderId) !== String(orderId));
            if (next.length === 0) setConfirmModalOpen(false);
            return next;
        });
    };

    const handleBulkSuccess = (orders) => {
        setSuccessModalActionType("Placement");
        let flat = [];
        if (Array.isArray(orders)) {
            orders.forEach(item => {
                if (item?.order_resp && Array.isArray(item.order_resp)) {
                    item.order_resp.forEach(o => {
                        flat.push({
                            clientId: item.client_id || o.clientId,
                            symbol: o.symbol || "N/A",
                            qty: o.qty || 0,
                            status: o.status === "failed" ? "Rejected" : (o.status === "success" ? "PENDING" : (o.status || "PENDING")),
                            orderId: o.response?.data?.order_id || o.response?.id || o.id || o.order_id || o.orderId || "PLACED",
                            broker: "alpaca"
                        });
                    });
                } else {
                    flat.push(item);
                }
            });
        }
        setSubmittedOrders(flat.length > 0 ? flat : orders);
        setSuccessModalOpen(true);
    };

    const handleOpenModal = (clientData) => {
        setSelectedClient(clientData);
        setModalOpen(true);
    };

    const handleOpenBulkModify = (orders) => {
        setOrdersToModify(orders);
        setBulkModifyOpen(true);
    };

    const handleSort = (key) => {
        setSortConfig(prev => ({
            key,
            direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
        }));
    };

    const getSortIcon = (key) => {
        if (sortConfig.key !== key)
            return <svg className="w-3 h-3 text-gray-500" viewBox="0 0 24 24" fill="none" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" /></svg>;
        return sortConfig.direction === "asc"
            ? <svg className="w-3 h-3 text-blue-400" viewBox="0 0 24 24" fill="none" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" /></svg>
            : <svg className="w-3 h-3 text-blue-400" viewBox="0 0 24 24" fill="none" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>;
    };

    const processedData = useMemo(() => {
        if (!data || data.length === 0) return [];
        let processed = [...data];
        if (searchQuery.trim()) {
            const q = searchQuery.toLowerCase();
            processed = processed.filter(row =>
                (row.broker || "").toLowerCase().includes(q) ||
                (row.clientId || "").toLowerCase().includes(q) ||
                (row.clientName || "").toLowerCase().includes(q)
            );
        }
        if (sortConfig.key) {
            processed.sort((a, b) => {
                let aValue, bValue;
                if (sortConfig.key.includes("_")) {
                    const [asset, prop] = sortConfig.key.split("_");
                    aValue = a[asset]?.[prop] ?? 0;
                    bValue = b[asset]?.[prop] ?? 0;
                } else {
                    aValue = a[sortConfig.key];
                    bValue = b[sortConfig.key];
                }
                if (aValue === null || aValue === undefined) return 1;
                if (bValue === null || bValue === undefined) return -1;
                if (typeof aValue === "number" && typeof bValue === "number")
                    return sortConfig.direction === "asc" ? aValue - bValue : bValue - aValue;
                const aStr = String(aValue).toLowerCase();
                const bStr = String(bValue).toLowerCase();
                return sortConfig.direction === "asc" ? (aStr < bStr ? -1 : 1) : (aStr > bStr ? -1 : 1);
            });
        }
        return processed;
    }, [data, sortConfig, searchQuery]);

    // Pending orders eligible for bulk modify
    const pendingOrdersForModify = useMemo(() => {
        return orders.filter(o => {
            if (String(o.broker || "").toLowerCase() !== "alpaca") return false;
            const s = String(o.status || "").toLowerCase();
            return ["new", "partially_filled", "accepted", "pending_new", "held", "pending_replace", "pending", "open"].includes(s) || String(o.status_no) === "6";
        });
    }, [orders]);

    if (loading) {
        return (
            <div className="bg-[#1a1d29] p-6 flex items-center justify-center min-h-[400px]">
                <p className="text-gray-400">Loading positions...</p>
            </div>
        );
    }

    return (
        <div className="flex flex-col h-full">
            {/* ── Toolbar ── */}
            <div className="flex justify-between items-center p-3 mb-3 bg-[#14161f] border-b border-gray-800/50 flex-wrap gap-4">
                {/* Left: Base Qty */}
                <div className="flex items-center gap-6 flex-wrap">
                    {baseQtyMap && Object.keys(baseQtyMap).length > 0 && (
                        <div className="flex items-center gap-3">
                            <span className="text-gray-400 font-bold text-[11px] uppercase tracking-wider">Base Qty:</span>
                            <div className="flex items-center gap-2">
                                {Object.entries(baseQtyMap).map(([asset, qty]) => (
                                    <span key={asset} className="bg-[#0f1116] border border-gray-800 px-2.5 py-1 rounded-md text-xs text-gray-300 shadow-inner">
                                        <span className="font-bold text-[#FCD72B]">{asset}:</span> {formatNumber(qty)}
                                    </span>
                                ))}
                            </div>
                        </div>
                    )}
                </div>

                {/* Right: Cancel + Action buttons */}
                <div className="flex items-center gap-4 flex-wrap ml-auto">
                    {Object.keys(cancelGroups.assets).length > 0 && (
                        <div className="flex items-center gap-2 pr-2">
                            {Object.entries(cancelGroups.assets).map(([asset, assetOrders]) => (
                                <button
                                    key={asset}
                                    onClick={() => openCancelModal(assetOrders)}
                                    className="px-3 py-1.5 bg-red-900/20 hover:bg-red-800/40 text-red-400 text-[10px] font-bold rounded border border-red-800/30 transition-all flex items-center gap-2 uppercase tracking-wide hover:shadow-lg hover:shadow-red-900/20"
                                >
                                    <span>Cancel {asset}</span>
                                    <span className="bg-red-950/50 px-1.5 rounded text-[10px]">{assetOrders.length}</span>
                                </button>
                            ))}
                        </div>
                    )}

                    {Object.keys(cancelGroups.assets).length > 0 && (
                        <div className="w-px h-6 bg-gray-700/50 hidden sm:block"></div>
                    )}

                    <div className="flex items-center gap-3">
                        {/* Bulk Modify */}
                        <button
                            onClick={() => setIsSelectionModalOpen(true)}
                            className="px-4 py-1.5 bg-indigo-600 hover:bg-indigo-500 text-white text-[11px] font-black uppercase tracking-wider rounded shadow-lg shadow-indigo-900/40 transition-all flex items-center gap-2 border border-indigo-400/30"
                        >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                            <span>Bulk SL Order Modify</span>
                        </button>

                        {/* Order Placement */}
                        <button
                            onClick={() => setBulkModalOpen(true)}
                            className="px-4 py-1.5 bg-blue-600 hover:bg-blue-500 text-white text-[11px] font-black uppercase tracking-wider rounded shadow-lg shadow-blue-900/40 transition-all flex items-center gap-2 border border-blue-400/30"
                        >
                            <span>Order Placement</span>
                        </button>
                    </div>
                </div>
            </div>

            {(!data || data.length === 0) ? (
                <div className="flex-1 flex flex-col items-center justify-center p-12 bg-[#1a1d29]/50">
                    <div className="text-center max-w-sm">
                        <div className="w-16 h-16 bg-[#FCD72B]/10 rounded-full flex items-center justify-center mx-auto mb-4 border border-[#FCD72B]/20">
                            <svg className="w-8 h-8 text-[#FCD72B]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                            </svg>
                        </div>
                        <h3 className="text-white text-lg font-bold mb-2">No Position Data</h3>
                        <p className="text-gray-400 text-sm mb-6">
                            No Alpaca positions found. Use the button above to place new orders.
                        </p>
                    </div>
                </div>
            ) : (
                <>
                    {/* Search */}
                    <div className="relative mb-2">
                        <input
                            type="text"
                            placeholder="Search broker / client id..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="bg-[#0f1116] border border-gray-700 text-gray-200 text-sm px-3 py-1.5 rounded w-64 ml-6 focus:outline-none focus:ring-1 focus:ring-[#FCD72B]/50"
                        />
                    </div>

                    {/* Scrollbar Styles */}
                    <style>{`
                        .sleek-scrollbar::-webkit-scrollbar { width: 8px; height: 8px; }
                        .sleek-scrollbar::-webkit-scrollbar-track { background: transparent; }
                        .sleek-scrollbar::-webkit-scrollbar-thumb { background: rgba(252,215,43,0.2); border-radius: 10px; }
                        .sleek-scrollbar::-webkit-scrollbar-thumb:hover { background: rgba(252,215,43,0.4); }
                    `}</style>

                    <div className="flex-1 overflow-auto sleek-scrollbar">
                        <table className="w-full text-sm border-collapse">
                            <thead className="sticky top-0 bg-[#14161f] z-10">
                                <tr className="border-b border-gray-700/50">
                                    <th rowSpan="2" className="text-center py-2 text-gray-400 font-medium bg-[#14161f] border-r border-gray-700/50 w-10">No.</th>
                                    <th rowSpan="2" onClick={() => handleSort("clientName")} className="text-left py-2 px-2 text-gray-300 font-medium bg-[#14161f] border-r border-gray-700/50 min-w-[180px] cursor-pointer hover:bg-[#1a1d29]">
                                        <div className="flex items-center gap-1"><span>Name</span>{getSortIcon("clientName")}</div>
                                    </th>
                                    {activeAssets.map(asset => (
                                        <th key={asset} colSpan="3" onClick={() => handleSort(asset)} className="text-center py-2 px-2 text-white font-semibold border-l border-gray-700/50 cursor-pointer hover:bg-[#1a1d29]">
                                            <div className="flex items-center justify-center gap-1"><span>{asset}</span>{getSortIcon(asset)}</div>
                                        </th>
                                    ))}
                                </tr>
                                <tr className="border-b border-gray-700/50">
                                    {activeAssets.map(asset => (
                                        <React.Fragment key={asset}>
                                            <th onClick={() => handleSort(`${asset}_multiplier`)} className="text-center py-1 px-1 text-gray-400 text-xs font-medium border-l border-gray-700/50 w-10 cursor-pointer hover:bg-[#1a1d29]" title="Multiplier">Mul</th>
                                            <th onClick={() => handleSort(`${asset}_qty`)} className="text-center py-1 px-1 text-gray-400 text-xs font-medium border-l border-gray-700/30 w-20 cursor-pointer hover:bg-[#1a1d29]">Qty</th>
                                            <th onClick={() => handleSort(`${asset}_slQty`)} className="text-center py-1 px-1 text-gray-400 text-xs font-medium border-l border-gray-700/30 w-16 cursor-pointer hover:bg-[#1a1d29]">SL</th>
                                        </React.Fragment>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {processedData.map((row, idx) => (
                                    <tr key={row.clientId || idx} className="border-b border-gray-800/50 hover:bg-[#14161f] transition-colors">
                                        <td className="py-1 text-center text-gray-500 bg-[#1a1d29] border-r border-gray-700/50">{idx + 1}</td>
                                        <td className="py-1.5 px-2 text-white font-medium border-r border-gray-700/50">
                                            <div className="flex justify-between items-center gap-2">
                                                <div className="flex items-center gap-2">
                                                    <span className={`px-1.5 py-0.5 rounded text-xs font-bold ${getBrokerBadge(row.broker).class}`}>{getBrokerBadge(row.broker).text}</span>
                                                    <span>{row.clientName} <span className="text-xs text-gray-500">({row.clientId})</span></span>
                                                </div>
                                                <button onClick={() => handleOpenModal(row)} className="text-blue-400 hover:text-blue-300">
                                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" /></svg>
                                                </button>
                                            </div>
                                        </td>
                                        {activeAssets.map(asset => {
                                            const cell = row[asset];
                                            if (!cell) {
                                                return (
                                                    <React.Fragment key={asset}>
                                                        <td className="py-1.5 px-1 text-center text-gray-600 border-l border-gray-700/50">-</td>
                                                        <td className="py-1.5 px-1 text-center text-gray-600 border-l border-gray-700/30">-</td>
                                                        <td className="py-1.5 px-1 text-center text-gray-600 border-l border-gray-700/30">-</td>
                                                    </React.Fragment>
                                                );
                                            }
                                            return (
                                                <React.Fragment key={asset}>
                                                    {/* Multiplier */}
                                                    <td className="py-1.5 px-1 text-center text-gray-300 border-l border-gray-700/50">
                                                        <OptimizedNumberCell value={cell.multiplier} className="text-center text-gray-300" />
                                                    </td>
                                                    {/* Net Qty */}
                                                    <td className="py-1.5 px-1 text-center font-medium border-l border-gray-700/30">
                                                        <OptimizedColorCell
                                                            value={cell.qty}
                                                            color={cell.color}
                                                            formatFn={formatNumber}
                                                            applyColorToCell={true}
                                                        />
                                                        {cell.netSignedQty !== undefined && cell.netSignedQty !== 0 && (
                                                            <span className={`ml-1 text-[8px] px-1 py-px rounded font-bold uppercase tracking-wide ${cell.netSignedQty > 0 ? "bg-green-500/10 text-green-400" : "bg-red-500/10 text-red-400"}`}>
                                                                {cell.netSignedQty > 0 ? "L" : "S"}
                                                            </span>
                                                        )}
                                                    </td>
                                                    {/* SL Qty */}
                                                    <td className="py-1.5 px-1 text-center font-medium border-l border-gray-700/30">
                                                        <OptimizedColorCell
                                                            value={cell.slQty ?? cell.sl_qty ?? null}
                                                            color={cell.color}
                                                            formatFn={formatNumber}
                                                            applyColorToCell={true}
                                                        />
                                                    </td>
                                                </React.Fragment>
                                            );
                                        })}
                                    </tr>
                                ))}
                                {processedData.length === 0 && (
                                    <tr>
                                        <td colSpan={2 + activeAssets.length * 3} className="py-8 text-center text-gray-500 font-medium">
                                            No Alpaca positions match your search.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </>
            )}

            {/* ── Modals ── */}
            {modalOpen && selectedClient && (
                <AlpacaPositionComparisonModal
                    isOpen={modalOpen}
                    onClose={() => setModalOpen(false)}
                    clientData={selectedClient}
                    positions={positions}
                    symbolStrategyBaseQty={symbolStrategyBaseQty}
                    orders={orders}
                />
            )}

            {bulkModalOpen && (
                <AlpacaBulkOrderModal
                    isOpen={bulkModalOpen}
                    onClose={() => setBulkModalOpen(false)}
                    users={users}
                    onSuccess={handleBulkSuccess}
                />
            )}

            {isSelectionModalOpen && (
                <AlpacaGroupSelectionModal
                    isOpen={isSelectionModalOpen}
                    onClose={() => setIsSelectionModalOpen(false)}
                    orders={pendingOrdersForModify}
                    onConfirm={(selected) => {
                        setIsSelectionModalOpen(false);
                        handleOpenBulkModify(selected);
                    }}
                />
            )}

            {bulkModifyOpen && (
                <AlpacaBulkModifyModal
                    isOpen={bulkModifyOpen}
                    onClose={() => setBulkModifyOpen(false)}
                    orders={ordersToModify}
                    onSuccess={(orders) => {
                        setBulkModifyOpen(false);
                        setSuccessModalActionType("Modify");
                        setSubmittedOrders(orders);
                        setSuccessModalOpen(true);
                    }}
                />
            )}

            {confirmModalOpen && (
                <CancelConfirmationModal
                    isOpen={confirmModalOpen}
                    onClose={() => setConfirmModalOpen(false)}
                    onConfirm={handleConfirmCancel}
                    orders={ordersToCancel}
                    onRemoveOrder={handleRemoveFromBatch}
                />
            )}

            {successModalOpen && (
                <AlpacaExitSuccessModal
                    isOpen={successModalOpen}
                    onClose={() => setSuccessModalOpen(false)}
                    actionType={successModalActionType}
                    orders={submittedOrders}
                />
            )}
        </div>
    );
}
